import { compact, map, filter, sortBy, get, isEmpty, identity, pickBy } from 'lodash';
import { FormInstance } from 'antd';

import { getFullStationName, isEcoplantAdmin, isEcoplantShowcase } from '@/utils/util-functions';
import { Role } from '@/store/AssociableEntitiesStore';

export interface FormValues {
  number: string;
  notificationsEnabled: boolean;
  stationAndSubscriptions: {
    stationId?: string;
    subscriptions?: {
      ShutdownNotification: {
        email: boolean;
        sms: boolean;
        status?: string;
      };
    };
  }[];
}

export enum MODES {
  CREATE = 'create',
  EDIT = 'edit',
}

export const FORM_DEFAULT_INITIAL_VALUES = {
  number: '',
  notificationsEnabled: true,
  stationAndSubscriptions: [
    {
      stationId: null,
      isFavorite: false,
      subscriptions: {
        ShutdownNotification: {
          email: true,
          sms: true,
        },
      },
    },
  ],
};

export const isAddStationButtonDisabled = (form, associableEntitiesStore) => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  return associableEntitiesStore.stations?.length === stationAndSubscriptions?.length;
};

export const isRoleSelectDisabledByRowIndex = (rowIndex, form) => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  const currentUserStation = stationAndSubscriptions[rowIndex];
  const isStationSelected = currentUserStation?.stationId;

  return !isStationSelected;
};

export const getStationIdByRow = (rowIndex, form): string => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  const currentUserStation = stationAndSubscriptions[rowIndex];
  return currentUserStation?.stationId;
};

export const isValueDashboardChecked = (rowIndex: number, form: FormInstance): boolean => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  const subscriptions = stationAndSubscriptions[rowIndex]?.subscriptions;
  return subscriptions?.ValueDashboardReport?.email || false;
};

export const getRolesByRowIndex = (rowIndex, form, associableEntitiesStore): Role[] => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  const stationId: string = stationAndSubscriptions[rowIndex]?.stationId;
  return associableEntitiesStore.getRolesByStationId(stationId);
};

export const hasUnselectPermission = (rowIndex, form, userStore, stationStore) => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  const currentUserStation = stationAndSubscriptions[rowIndex];
  const stationId = currentUserStation?.stationId;
  return isEcoplantAdmin(userStore, stationStore, stationId) || isEcoplantShowcase(userStore, stationStore, stationId);
};

export const getAssociableStationsByRowIndex = (rowIndex, form, associableEntitiesStore) => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  const filteredStationAndSubscriptions = compact(stationAndSubscriptions);
  const selectedStationsIds = map(filteredStationAndSubscriptions, ({ stationId }) => stationId);
  const currentSelectedStationId = selectedStationsIds[rowIndex];
  const filterdSelectedStationsIds = filter(selectedStationsIds, selectedStationsId => {
    return selectedStationsId !== currentSelectedStationId;
  });
  const filteredAssociableEntities = filter(associableEntitiesStore.stations, associableEntity => {
    return !filterdSelectedStationsIds.includes(associableEntity.id);
  });

  return sortBy(filteredAssociableEntities, ['customerName', 'siteName', 'name']);
};

export const isDisableValueDashboardReportByRow = (
  rowIndex: number,
  form: FormInstance,
  valueDashboardStation: { id: string }[],
): boolean => {
  const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
  const stationId = stationAndSubscriptions[rowIndex]?.stationId;
  return !valueDashboardStation?.some(station => station.id === stationId);
};

export const prepareNotificationSubscriptions = (stationAndSubscriptions: FormValues['stationAndSubscriptions']) => {
  return map(stationAndSubscriptions, stationAndSubscription => {
    const presets = map(stationAndSubscription.subscriptions, (value, key) => {
      if (key === 'ValueDashboardReport') {
        if (value.status) {
          key = value.status;
          delete value.status;
        } else {
          key = 'ValueDashboardWeekly';
        }
      }
      const valuesWithoutUndefined = pickBy(value, identity);
      return {
        notificationType: key,
        preferredChannels: Object.keys(valuesWithoutUndefined),
      };
    });

    const cleanPresets = filter(presets, preset => {
      return !isEmpty(preset.preferredChannels);
    });

    return {
      stationId: stationAndSubscription.stationId,
      presets: cleanPresets,
    };
  });
};

const basicFieldsMapping = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  language: 'language',
  measurementSystem: 'measurementSystem',
  number: 'phoneNumber.number',
  cc: 'phoneNumber.countryCode',
  notificationsEnabled: 'notificationsEnabled',
};

const hasUserUpdatedStationSubscriptions = (initialValues, payload) => {
  const convertedInitialSubs = prepareNotificationSubscriptions(initialValues.stationAndSubscriptions);

  if (initialValues.stationAndSubscriptions.length !== payload.userStations.length) {
    return true;
  }

  const initialStationsMap = Object.fromEntries(
    initialValues.stationAndSubscriptions.map(station => [station.stationId, station]),
  );

  return payload.userStations?.some(payloadUserStation => {
    const initialUserStation = initialStationsMap[payloadUserStation.station.id];
    const currentStationSubscriptions = payload.notificationSubscriptions.find(
      sub => sub.stationId === payloadUserStation.station.id,
    );

    if (
      !initialUserStation ||
      initialUserStation.roleId !== payloadUserStation.role.id ||
      initialUserStation.stationId !== payloadUserStation.station.id ||
      initialUserStation.isFavorite !== payloadUserStation.isFavorite
    ) {
      return true;
    }

    const initialSubs = convertedInitialSubs.find(sub => sub.stationId === initialUserStation.stationId)?.presets;
    const payloadSubs = currentStationSubscriptions?.presets || {};

    return JSON.stringify(initialSubs) !== JSON.stringify(payloadSubs);
  });
};
export const hasUserUpdatedAnyField = (initialValues, payload) => {
  if (Object.entries(basicFieldsMapping).some(([key, value]) => get(initialValues, key) !== get(payload, value))) {
    return true;
  }

  return hasUserUpdatedStationSubscriptions(initialValues, payload);
};

export const stationNameMatchesSearchTerm = (stationStore, stationAndSubscription, searchTerm) => {
  if (!searchTerm) return true;

  if (!stationAndSubscription?.stationId) return false;

  const station = stationStore.getStationById(stationAndSubscription.stationId);

  if (!station) return false;

  const stationName = getFullStationName(station);
  return stationName.toLowerCase().includes(searchTerm.toLowerCase());
};

export const getStationName = (stationId: string, stationLookupMap: Record<string, { fullName: string }>): string => {
  if (!stationId) return '';
  const station = stationLookupMap[stationId];
  if (!station) return stationId;
  return station.fullName || getFullStationName(station);
};

export const getSkeletonProps = (isReady: boolean) => ({
  active: true,
  title: false,
  paragraph: { rows: 1, width: 140 },
  loading: !isReady,
});

export const getMappedUserStations = stationAndSubscriptions =>
  stationAndSubscriptions.map(stationAndSubscription => ({
    station: { id: stationAndSubscription.stationId },
    role: { id: stationAndSubscription.roleId },
  }));

export const getMappedUserFavoriteStations = stationAndSubscriptions =>
  stationAndSubscriptions
    .filter(stationAndSubscription => stationAndSubscription.isFavorite)
    .map(stationAndSubscription => ({
      station: { id: stationAndSubscription.stationId },
    }));
